import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  DatePicker as MuiDatePicker,
  Icon,
  Badge,
  PickersDay,
} from "investira.react.components";

const CalendarIcon = () => <Icon iconName="calendar" size={21} />;

/**
 * Renderiza o componente DatePicker.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Object} props.slotProps - Props adicionais repassadas aos slots internos.
 * @param {Object} props.restProps - Props adicionais repassadas para `restProps`.
 * @returns {JSX.Element} Elemento renderizado pelo componente.
 */
const normalizeHighlightedDate = (pDate) => {
  const xDate = dayjs.isDayjs(pDate)
    ? pDate
    : dayjs(pDate?.data || pDate?.value || pDate);

  return xDate.isValid() ? xDate.format("YYYY-MM-DD") : null;
};

const normalizeAvailableDate = (pDate) => {
  const xDate = dayjs.isDayjs(pDate)
    ? pDate
    : dayjs(pDate?.data || pDate?.value || pDate);

  return xDate.isValid() ? xDate : null;
};

function HighlightedDay(props) {
  const {
    highlightedDatesSet,
    day,
    outsideCurrentMonth = false,
    ...other
  } = props;

  const isHighlighted =
    !outsideCurrentMonth &&
    highlightedDatesSet instanceof Set &&
    highlightedDatesSet.has(day.format("YYYY-MM-DD"));

  return (
    <Badge
      key={day.toString()}
      color="warning"
      invisible={!isHighlighted}
      overlap="circular"
      variant="dot"
      size="small"
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
      />
    </Badge>
  );
}

const DatePicker = React.forwardRef((props, ref) => {
  const {
    slotProps,
    slots,
    value,
    onChange,
    minDate,
    maxDate,
    shouldDisableDate,
    highlightedDates = [],
    availableDates,
    selectionOnly = false,
    ...restProps
  } = props;

  const xAvailableDates = useMemo(() => {
    if (!Array.isArray(availableDates)) return [];

    return availableDates
      .map(normalizeAvailableDate)
      .filter((xDate) => xDate !== null);
  }, [availableDates]);

  const xAvailableDatesSet = useMemo(() => {
    return new Set(
      xAvailableDates.map((xDate) => xDate.format("YYYY-MM-DD"))
    );
  }, [xAvailableDates]);

  const xMinAvailableDate = useMemo(() => {
    if (xAvailableDates.length === 0) return null;

    return xAvailableDates.reduce((xMinDate, xCurrentDate) => {
      return xCurrentDate.isBefore(xMinDate, "day") ? xCurrentDate : xMinDate;
    }, xAvailableDates[0]);
  }, [xAvailableDates]);

  const xMaxAvailableDate = useMemo(() => {
    if (xAvailableDates.length === 0) return null;

    return xAvailableDates.reduce((xMaxDate, xCurrentDate) => {
      return xCurrentDate.isAfter(xMaxDate, "day") ? xCurrentDate : xMaxDate;
    }, xAvailableDates[0]);
  }, [xAvailableDates]);

  const xHighlightedDatesSource =
    highlightedDates.length > 0 ? highlightedDates : xAvailableDates;

  const xHighlightedDatesSet = useMemo(() => {
    return new Set(
      xHighlightedDatesSource
        .map(normalizeHighlightedDate)
        .filter((xDate) => xDate !== null)
    );
  }, [xHighlightedDatesSource]);

  const xResolvedShouldDisableDate = (pDate) => {
    const xIsDisabledByAvailableDates =
      xAvailableDatesSet.size > 0 &&
      !xAvailableDatesSet.has(pDate.format("YYYY-MM-DD"));

    const xIsDisabledByConsumer =
      typeof shouldDisableDate === "function"
        ? shouldDisableDate(pDate)
        : false;

    return xIsDisabledByAvailableDates || xIsDisabledByConsumer;
  };

  const xShouldHighlightDates =
    xHighlightedDatesSet.size > 0 && !slots?.day;

  const xSlotProps = {
    ...slotProps,
    ...(selectionOnly && {
      field: (ownerState) => {
        const xFieldSlotProps =
          typeof slotProps?.field === "function"
            ? slotProps.field(ownerState)
            : slotProps?.field || {};

        return {
          ...xFieldSlotProps,
          readOnly: true,
        };
      },
    }),
    ...(xShouldHighlightDates && {
      day: (ownerState) => {
        const xDaySlotProps =
          typeof slotProps?.day === "function"
            ? slotProps.day(ownerState)
            : slotProps?.day || {};

        return {
          ...xDaySlotProps,
          highlightedDatesSet: xHighlightedDatesSet,
        };
      },
    }),
  };

  useEffect(() => {
    if (xAvailableDatesSet.size === 0 || !onChange) return;

    const xCurrentValue = dayjs.isDayjs(value)
      ? value
      : dayjs(value);

    const xHasValidCurrentValue =
      xCurrentValue.isValid() &&
      xAvailableDatesSet.has(xCurrentValue.format("YYYY-MM-DD"));

    if (!xHasValidCurrentValue && xMaxAvailableDate) {
      onChange(xMaxAvailableDate);
    }
  }, [value, onChange, xAvailableDatesSet, xMaxAvailableDate]);

  return (
    <MuiDatePicker
      ref={ref}
      value={value}
      onChange={onChange}
      minDate={minDate || xMinAvailableDate}
      maxDate={maxDate || xMaxAvailableDate}
      shouldDisableDate={xResolvedShouldDisableDate}
      slots={{
        openPickerIcon: CalendarIcon,
        ...slots,
        ...(xShouldHighlightDates && { day: HighlightedDay }),
      }}
      slotProps={xSlotProps}
      {...restProps}
    />
  );
});

DatePicker.propTypes = {
  availableDates: PropTypes.array,
  highlightedDates: PropTypes.array,
  selectionOnly: PropTypes.bool,
};

export default DatePicker;
